/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { ListItem } from '@veneer/core';
import React, { memo, useCallback, useLayoutEffect, useRef } from 'react';
import Balancer from 'react-wrap-balancer';
import { debounce } from '../../base/helper';
import './styles.scss';
import { StatusListItemProps } from './types';

/**
 * UI Toolkit : Status List Item
 * @public
 */
const TStatusListItem: React.FC<StatusListItemProps> = ({
  id,
  startIcon,
  statusLabel,
  endIconList,
  statusType = 'STATUS_GENERIC',
  onClick,
  ...rest
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const HandleResize = useCallback(() => {
    if (divRef && divRef.current) divRef.current.style.maxWidth = '220px';
  }, []);

  useLayoutEffect(() => {
    const debouncedHandleResize = debounce(HandleResize, 200);
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [HandleResize]);

  return (
    <ListItem
      id={`${id}__list-item`}
      role="button"
      onClick={onClick}
      className={`clientos-ui-toolkit-status-list__item clientos-ui-toolkit-status-list__item--${statusType}
        ${
          onClick
            ? 'clientos-ui-toolkit-status-list__item--clickable'
            : 'clientos-ui-toolkit-status-list__item--non-clickable'
        }
      `}
      leadingArea={
        startIcon && (
          <div
            className="clientos-ui-toolkit-status-list__leading-area"
            id={`${id}__leading-area`}
          >
            {startIcon}
          </div>
        )
      }
      centerArea={
        <div
          className={`clientos-ui-toolkit-status-list__status-label caption-large`}
          id={`${id}__status-label`}
          aria-label={statusLabel}
        >
          <Balancer ref={divRef}>{statusLabel}</Balancer>
        </div>
      }
      trailingArea={
        endIconList && (
          <div
            className="clientos-ui-toolkit-status-list__trailing-area"
            id={`${id}__trailing-area`}
          >
            {endIconList}
          </div>
        )
      }
      {...rest}
    />
  );
};

export const StatusListItemAnalytics = withAnalytics(TStatusListItem);
export default memo(TStatusListItem);
